/**
 * Chat API layer for sending messages and handling streaming responses
 * Follows established patterns from conversations.ts and backend expectations
 */

import { type MutationOptions } from '@tanstack/react-query';
import { apiClient } from './base/client';
import { ApiError } from './base/errors';
import { acquireBearerToken, msalInstance } from './auth';
import type { ChatAttachment } from '../stores/chatStore';
import { useStreamingStore } from '../stores/streamingStore';
import type { ConversationDetailResponse, DigitalMaintenanceStatus } from './conversations';
import { getAllConversationFiles } from './conversations';
import { DEFAULT_MODEL } from '../config/defaults';

/**
 * Data point interface for message citations
 */
export interface DataPoint {
  id: string;
  index: number;
  title: string;
  content: string;
  url?: string;
  source: string;
  snippet?: string;
  preview?: string;
}

/**
 * Send message request interface
 */
export interface SendMessageRequest {
  conversationId: string;
  message: string;
  history?: Array<{ user: string; bot: string }>; // Add history parameter
  currentMessageUploadedFiles?: string[]; // Add current message uploaded files parameter
  abortController?: AbortController; // Add AbortController for request cancellation
  sendMode?: 'digitalMaintenanceCheck'; // UI-only; never included in the wire payload
  options?: {
    agents?: string[];
    model?: string;
    temperature?: number;
    tools?: Array<{ name: string; filters?: string[] }>;
    useFiles?: boolean;
    semanticRanker?: boolean;
    top?: number;
    nativeSearch?: boolean;
    showPreviewFeatures?: boolean;
    hasGenieAccess?: boolean;
    genieSpaceNames?: string[];
    genieSpaceConversationIds?: Record<string, string>;
    foundryRagConversationIds?: Record<string, string>;
    websearchThreadId?: string;
    websearchConversationId?: string;
    uploadSessionId?: string;
    conversationTitle?: string;
    category?: Record<string, unknown>;
    uploadedFiles?: string[];
    currentStreamId?: string;
  };
}

/**
 * Send message response interface matching backend response
 */
export interface SendMessageResponse {
  message_id: string;
  answer: string;
  digital_maintenance_status?: DigitalMaintenanceStatus;
  data_points?: DataPoint[];
  thoughts?: string;
  error_code?: string;
  agent_classification?: string[];
  genie_conversation_ids?: Record<string, unknown>;
  foundry_rag_conversation_ids?: Record<string, string>;
  websearch_conversation_id?: string;
  conversationID?: string;
  user_input?: string;
  response_time?: number;
  token_count?: number;
  genie_space_names?: string[];
  genie_conversation_id?: string;
  genie_message_id?: string;
  toolCalls?: Array<{ id?: string; agent: string; tool?: string; args?: Record<string, unknown>; content: string; status: string; startedAt: number; completedAt?: number }>;
}

/**
 * Custom error class for chat-specific errors
 */
export class ChatApiError extends ApiError {
  public readonly name: string = 'ChatApiError';

  constructor(message: string, status?: number, code?: string) {
    super(message, status, code);
    Object.setPrototypeOf(this, ChatApiError.prototype);
  }
}

/**
 * Core mutation function for sending chat messages
 * Sends via WebSocket for streaming, with the response returned when the 'done' message arrives.
 */
export async function sendChatMessage(request: SendMessageRequest): Promise<SendMessageResponse> {
  try {
    if (!request.conversationId) {
      throw new ChatApiError(
        'Conversation ID is required',
        400,
        'MISSING_CONVERSATION_ID'
      );
    }

    if (!request.message?.trim()) {
      throw new ChatApiError(
        'Message content is required',
        400,
        'MISSING_MESSAGE'
      );
    }

    // Build the query payload following backend expectations
    const history = request.history || [];
    const lastEntry = history[history.length - 1];
    const isMessageAlreadyInHistory = lastEntry?.user === request.message && lastEntry?.bot === "";
    const currentMessageUploadedFiles = request.currentMessageUploadedFiles || [];

    const historyWithCurrentMessage = isMessageAlreadyInHistory
      ? history
      : [...history, { user: request.message, bot: "", uploaded_files: currentMessageUploadedFiles }];

    const currentStreamId = request.options?.currentStreamId ?? crypto.randomUUID();
    const queryPayload = {
      history: historyWithCurrentMessage,
      overrides: {
        semantic_ranker: request.options?.semanticRanker ?? true,
        top: request.options?.top ?? 50,
        temperature: request.options?.temperature ?? 0,
        gpt_version: request.options?.model ?? DEFAULT_MODEL,
        native_search: request.options?.nativeSearch ?? true,
        agent_type: "openaifunctions",
        specificAgents: request.options?.agents ?? [],
        llm_model_name: "azureopenai",
        tools: request.options?.tools ?? [{ name: "acs" }],
        showPreviewFeatures: request.options?.showPreviewFeatures ?? true,
        has_genie_access: request.options?.hasGenieAccess ?? false,
        genie_space_names: request.options?.genieSpaceNames ?? [],
        genie_space_conversation_IDs: request.options?.genieSpaceConversationIds ?? {},
        foundry_rag_conversation_ids: request.options?.foundryRagConversationIds ?? {},
        websearch_conversation_id: request.options?.websearchConversationId ?? "",
        use_files: request.options?.useFiles ?? false,
        stream_response: true,
      },
      uploadSessionId: request.options?.uploadSessionId ?? "",
      conversationTitle: request.options?.conversationTitle ?? request.message.slice(0, 200),
      category: request.options?.category ?? {},
      uploadedFiles: request.options?.uploadedFiles ?? [],
      currentStreamId,
      conversation_id: request.conversationId,
      session_id: window.currentSessionId,
      timezoneOffset: new Date().getTimezoneOffset(),
    };

    // Reuse the chat WebSocket across messages; each payload carries its own
    // stream id and conversation id so start/done semantics stay per-message.
    const { socket } = await useStreamingStore.getState().ensureConnected();

    // Clear stale state and mark which assistant message owns this stream.
    const { startStream, sendChat } = useStreamingStore.getState();
    useStreamingStore.setState({ lastDoneResponse: null, currentStreamRecord: null });
    startStream(currentStreamId);

    if (socket.readyState !== WebSocket.OPEN) {
      throw new ChatApiError(
        'WebSocket not connected',
        undefined,
        'WS_NOT_CONNECTED'
      );
    }

    // Subscribe before sending so very fast `done` responses cannot be missed.
    const donePromise = new Promise<Record<string, any>>((resolve, reject) => {
      let settled = false;
      let unsub: () => void = () => {};
      const cleanup = () => {
        settled = true;
        unsub();
      };

      unsub = useStreamingStore.subscribe((state, prev) => {
        if (settled) return;
        if (state.lastDoneResponse && state.lastDoneResponse !== prev.lastDoneResponse) {
          cleanup();
          resolve(state.lastDoneResponse);
          return;
        }
        if ((state as any).lastStreamError && (state as any).lastStreamError !== (prev as any).lastStreamError) {
          const streamError = (state as any).lastStreamError as Record<string, any>;
          cleanup();
          reject(new ChatApiError(
            streamError.detail || 'Chat stream failed',
            undefined,
            streamError.error_code || 'STREAM_ERROR'
          ));
          return;
        }

        if (state.connectionStatus === 'disconnected' && !state.lastDoneResponse &&
            prev.connectionStatus !== 'disconnected') {
          const record = state.currentStreamRecord;
          if (state.cancelledStreamId === currentStreamId || state.cancelledStreamId === record?.messageId) {
            cleanup();
            reject(new ChatApiError('Connection cancelled', undefined, 'REQUEST_CANCELLED'));
            return;
          }

          // Fallback: use streamed content if available instead of rejecting
          if (record?.answer?.trim()) {
            cleanup();
            resolve({
              answer: record.answer,
              toolCalls: record.toolCalls?.map(tc => ({
                id: tc.id, agent: tc.agent, tool: tc.tool, args: tc.args, content: tc.content, status: tc.status, startedAt: tc.startedAt, completedAt: tc.completedAt,
              })) || [],
            });
          } else {
            cleanup();
            reject(new ChatApiError('WebSocket disconnected during chat', undefined, 'WS_DISCONNECTED'));
          }
        }
      });

      const currentState = useStreamingStore.getState();
      if (currentState.lastDoneResponse) {
        cleanup();
        resolve(currentState.lastDoneResponse);
        return;
      }

      // Handle abort
      if (request.abortController) {
        request.abortController.signal.addEventListener('abort', () => {
          if (!settled) {
            cleanup();
            const { cancelChat } = useStreamingStore.getState();
            cancelChat();
            reject(new ChatApiError('Connection cancelled', undefined, 'REQUEST_CANCELLED'));
          }
        });
      }
    });

    // Send the chat message over WS after the done subscription is active.
    sendChat(queryPayload);

    const data = await donePromise;

    return {
      message_id: data.message_id || '',
      answer: data.answer || '',
      digital_maintenance_status: data.digital_maintenance_status,
      data_points: data.data_points || [],
      thoughts: data.thoughts,
      error_code: data.error_code,
      agent_classification: data.agent_classification,
      genie_conversation_ids: data.genie_conversation_ids,
      foundry_rag_conversation_ids: data.foundry_rag_conversation_ids,
      websearch_conversation_id: data.websearch_conversation_id,
      conversationID: data.conversationID || request.conversationId,
      user_input: data.user_input || request.message,
      response_time: data.response_time,
      token_count: data.token_count,
      genie_space_names: data.genie_space_names,
      genie_conversation_id: data.genie_conversation_id,
      genie_message_id: data.genie_message_id,
      toolCalls: data.toolCalls || [],
    };
  } catch (error) {
    if (error instanceof ChatApiError) {
      throw error;
    }

    if (error instanceof Error && error.name === 'AbortError') {
      throw new ChatApiError(
        'Connection cancelled',
        undefined,
        'REQUEST_CANCELLED'
      );
    }

    throw new ChatApiError(
      `Failed to send message: ${error instanceof Error ? error.message : 'Unknown error'}`,
      undefined,
      'SEND_FAILED'
    );
  }
}

// TODO: Currently TanStack Query Keys and Options are not used - do we need to refactor chatUtils to use these, or remove them?
/**
 * TanStack Query mutation options for sending messages
 * Provides reusable, type-safe mutation configuration
 */
export const sendMessageMutationOptions = (): MutationOptions<SendMessageResponse, Error, SendMessageRequest> => ({
  mutationFn: sendChatMessage,
  // Retry configuration
  retry: (failureCount: number, error: any) => {
    // Don't retry authentication errors
    if (error instanceof ChatApiError && error.status === 401) {
      return false;
    }

    // Don't retry validation errors (4xx)
    if (error instanceof ChatApiError && error.status && error.status >= 400 && error.status < 501) {
      return false;
    }

    // Retry network errors and server errors up to 2 times
    return failureCount < 2;
  },
  retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 5000), // Exponential backoff
  // Mutation metadata
  meta: {
    actionType: 'send-message',
  },
});

/**
 * Query key factory for chat operations
 * Provides consistent cache keys for TanStack Query
 */
export const chatQueryKeys = {
  all: ['chat'] as const,
  messages: (conversationId: string) => [...chatQueryKeys.all, 'messages', conversationId] as const,
  optimistic: (conversationId: string) => [...chatQueryKeys.messages(conversationId), 'optimistic'] as const,
};

/**
 * Utility functions for chat operations
 */
export const chatUtils = {
  /**
   * Transform conversation history from frontend format to backend format
   */
  transformHistoryToBackendFormat: (conversationHistory: any[]): Array<{ user: string; bot: string; uploaded_files?: string[] }> => {
    return conversationHistory.map((msg: any) => ({
      user: msg.user || '',
      bot: msg.bot || '',
      uploaded_files: msg.uploadedFiles || msg.uploaded_files || [],
    }));
  },

  /**
   * Get conversation title from conversation history
   * Returns the first user message as the title, or fallback to current message
   */
  getConversationTitle: (conversationHistory: any[] | undefined, fallbackMessage: string): string => {
    if (conversationHistory && conversationHistory.length > 0) {
      // Use the first message as the conversation title
      const firstMessage = conversationHistory[0].user;
      if (firstMessage && firstMessage.trim()) {
        return firstMessage.slice(0, 200);
      }
    }
    // Fallback to current message for new conversations
    return fallbackMessage.slice(0, 200);
  },
  /**
   * Generate a unique message ID for optimistic updates
   */
  generateOptimisticMessageId: (): string => {
    return `optimistic-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  },

  /**
   * Create optimistic user message
   */
  createOptimisticUserMessage: (message: string, conversationId: string) => ({
    id: chatUtils.generateOptimisticMessageId(),
    role: 'user' as const,
    content: message,
    timestamp: new Date(),
    metadata: {
      conversationId,
      isOptimistic: true,
    },
    actions: {
      canEdit: true,
      canDelete: true,
      canRegenerate: false,
      canPlayAudio: false,
      isBookmarked: false,
      isLiked: false,
      isDisliked: false,
    },
  }),

  /**
   * Create optimistic assistant message placeholder
   */
  createOptimisticAssistantMessage: (conversationId: string) => ({
    id: chatUtils.generateOptimisticMessageId(),
    role: 'assistant' as const,
    content: '', // Will be populated by streaming or final response
    timestamp: new Date(),
    metadata: {
      conversationId,
      isOptimistic: true,
      isLoading: true,
    },
    actions: {
      canEdit: false,
      canDelete: false,
      canRegenerate: false,
      canPlayAudio: false,
      isBookmarked: false,
      isLiked: false,
      isDisliked: false,
    },
  }),

  /**
   * Transform API response to frontend message format
   */
  transformApiResponseToMessage: (response: SendMessageResponse) => ({
    id: response.message_id,
    role: 'assistant' as const,
    content: response.answer,
    timestamp: new Date(),
    metadata: {
      conversationId: response.conversationID,
      dataPoints: response.data_points,
      thoughts: response.thoughts,
      agentClassification: response.agent_classification,
      genieConversationIds: response.genie_conversation_ids,
      responseTime: response.response_time,
      tokenCount: response.token_count,
      isOptimistic: false,
      warning: {
        show: true,
        message: 'AI may make mistakes. Check important info.',
        type: 'info' as const,
      },
    },
    actions: {
      canEdit: false,
      canDelete: true,
      canRegenerate: true,
      canPlayAudio: true,
      isBookmarked: false,
      isLiked: false,
      isDisliked: false,
    },
  }),

  /**
   * Analyze file state from conversation data, upload session, and attachments
   * Follows Context7 pattern for comprehensive state analysis
   */
  analyzeFileState: (params: {
    conversationData?: ConversationDetailResponse;
    uploadSessionId?: string;
    attachments?: ChatAttachment[];
    conversationFiles?: string[];
    pendingFilesForMessage?: string[];
  }): {
    shouldUseFiles: boolean;
    allFiles: string[];
    uploadSessionId: string;
  } => {
    const {
      conversationData,
      uploadSessionId,
      attachments,
      conversationFiles = [],
      pendingFilesForMessage = [],
    } = params;

    // Get existing files from conversation data using message-level files
    const existingFiles = conversationData ? getAllConversationFiles(conversationData) : [];

    // Get new files from upload session/attachments
    const hasNewUploadSession = !!uploadSessionId;
    const completedAttachments = attachments?.filter(attachment => attachment.uploadStatus === 'completed') || [];
    const newFiles = completedAttachments.map(attachment => attachment.file.name);

    // Merge all known sources of filenames so we can reference uploads immediately
    const mergedFiles = Array.from(new Set([
      ...existingFiles,
      ...conversationFiles,
      ...newFiles,
      ...pendingFilesForMessage,
    ].filter((name): name is string => Boolean(name && name.trim()))));

    // Determine if files should be used
    const shouldUseFiles = mergedFiles.length > 0 || hasNewUploadSession;

    return {
      shouldUseFiles,
      allFiles: mergedFiles,
      uploadSessionId: uploadSessionId || ''
    };
  },
};
